import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import {
  Briefcase,
  Beaker,
  FolderKanban,
  Users,
  AlertTriangle,
  CalendarClock,
  TrendingUp,
  Euro,
  ArrowRight,
  Sparkles,
  Zap,
  Target,
  Leaf,
  Download,
  CheckCircle2,
  Clock,
  TrendingDown,
  Activity,
} from "lucide-react";

// ==================== BUDGET COMPARISON COMPONENT ====================
function BudgetComparisonPanel({ executive }) {
  const budget = executive.innovation_budget_2026;
  const oi = budget?.breakdown?.open_innovation;
  const rd = budget?.breakdown?.rd_projects;

  // Calculate execution rates
  const oiRate = oi?.estimated > 0 ? (oi.actual / oi.estimated) * 100 : 0;
  const rdRate = rd?.estimated > 0 ? (rd.actual / rd.estimated) * 100 : 0;
  const totalRate = budget?.execution_rate || 0;

  // Semáforo de ejecución de presupuesto (umbrales fijos, no dependen del mes)
  const getTrafficLight = (rate) => {
    if (rate > 85) return { color: "red", label: "Alerta: presupuesto casi agotado", bg: "bg-red-50 dark:bg-red-950/30", border: "border-red-200", text: "text-red-700 dark:text-red-400", bar: "bg-red-500" };
    if (rate > 60) return { color: "yellow", label: "Atención: ejecución acelerada", bg: "bg-amber-50 dark:bg-amber-950/30", border: "border-amber-200", text: "text-amber-700 dark:text-amber-400", bar: "bg-amber-500" };
    if (rate >= 5) return { color: "green", label: "Ejecución en rango normal", bg: "bg-emerald-50 dark:bg-emerald-950/30", border: "border-emerald-200", text: "text-emerald-700 dark:text-emerald-400", bar: "bg-emerald-500" };
    return { color: "gray", label: "Ejecución muy baja – revisar activación", bg: "bg-slate-50 dark:bg-slate-900/50", border: "border-slate-200", text: "text-slate-600 dark:text-slate-400", bar: "bg-slate-400" };
  };

  const oiLight = getTrafficLight(oiRate);
  const rdLight = getTrafficLight(rdRate);
  const totalLight = getTrafficLight(totalRate);

  const formatBudget = (v) => {
    if (!v && v !== 0) return "0€";
    if (Math.abs(v) >= 900000) return `${(v / 1000000).toFixed(2)}M€`;
    return `${(v / 1000).toFixed(0)}K€`;
  };

  // % de ejecución esperado = fracción del año 2026 ya transcurrida a fecha de hoy
  // (antes hardcodeado a "18% en Mar", se quedó congelado el resto del año — fix 2026-08-24).
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const dayOfYear = Math.floor((now - startOfYear) / 86400000) + 1;
  const expectedFraction = Math.min(1, dayOfYear / 365);
  const monthLabel = now.toLocaleDateString("es-ES", { month: "short" }).replace(".", "");

  const deviation = budget?.actual - (budget?.estimated * expectedFraction);
  const deviationText = Math.abs(deviation) > 10000
    ? `${deviation > 0 ? "+" : ""}${formatBudget(deviation)} vs esperado (${Math.round(expectedFraction * 100)}% en ${monthLabel})`
    : "Ejecución alineada con lo esperado a fecha de hoy";

  return (
    <div className="space-y-3" data-testid="budget-comparison-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold flex items-center gap-2">
          <Activity className="w-4 h-4 text-primary" />
          Comparativa Presupuesto 2026 · Open Innovation vs I+D
        </h3>
        {totalLight.color !== "green" && (
          <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${totalLight.bg} ${totalLight.text} border ${totalLight.border}`}>
            <AlertTriangle className="w-3 h-3" />
            {totalLight.label}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Open Innovation */}
        <Card className={`border ${oiLight.border} ${oiLight.bg}`}>
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-sm">Open Innovation</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${oiLight.bg} ${oiLight.text} border ${oiLight.border}`}>
                {oiRate.toFixed(1)}% ejec.
              </span>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Ejecutado: <strong className="text-foreground">{formatBudget(oi?.actual)}</strong></span>
                <span>Prev: {formatBudget(oi?.estimated)}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
                <div className={`h-full rounded-full transition-all ${oiLight.bar}`} style={{ width: `${Math.min(oiRate, 100)}%` }} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-1 text-center text-xs">
              <div className="bg-white/60 dark:bg-black/20 rounded p-1">
                <div className="font-semibold text-green-600">{oi?.items?.executed || 0}</div>
                <div className="text-muted-foreground">Ej.</div>
              </div>
              <div className="bg-white/60 dark:bg-black/20 rounded p-1">
                <div className="font-semibold text-amber-600">{oi?.items?.partial || 0}</div>
                <div className="text-muted-foreground">Parc.</div>
              </div>
              <div className="bg-white/60 dark:bg-black/20 rounded p-1">
                <div className="font-semibold text-slate-500">{oi?.items?.pending || 0}</div>
                <div className="text-muted-foreground">Pend.</div>
              </div>
            </div>
            <p className={`text-xs ${oiLight.text}`}>{oiLight.label}</p>
          </CardContent>
        </Card>

        {/* I+D Projects */}
        <Card className={`border ${rdLight.border} ${rdLight.bg}`}>
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-sm">Proyectos I+D</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${rdLight.bg} ${rdLight.text} border ${rdLight.border}`}>
                {rdRate.toFixed(1)}% ejec.
              </span>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Ejecutado: <strong className="text-foreground">{formatBudget(rd?.actual)}</strong></span>
                <span>Prev: {formatBudget(rd?.estimated)}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
                <div className={`h-full rounded-full transition-all ${rdLight.bar}`} style={{ width: `${Math.min(rdRate, 100)}%` }} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-1 text-center text-xs">
              <div className="bg-white/60 dark:bg-black/20 rounded p-1">
                <div className="font-semibold text-green-600">{rd?.items?.executed || 0}</div>
                <div className="text-muted-foreground">Ej.</div>
              </div>
              <div className="bg-white/60 dark:bg-black/20 rounded p-1">
                <div className="font-semibold text-amber-600">{rd?.items?.partial || 0}</div>
                <div className="text-muted-foreground">Parc.</div>
              </div>
              <div className="bg-white/60 dark:bg-black/20 rounded p-1">
                <div className="font-semibold text-slate-500">{rd?.items?.pending || 0}</div>
                <div className="text-muted-foreground">Pend.</div>
              </div>
            </div>
            <p className={`text-xs ${rdLight.text}`}>{rdLight.label}</p>
          </CardContent>
        </Card>

        {/* Total + Deviation */}
        <Card>
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-sm">Total 2026</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${totalLight.bg} ${totalLight.text} border ${totalLight.border}`}>
                {totalRate.toFixed(1)}% global
              </span>
            </div>
            <div className="text-center py-2">
              <div className="text-3xl font-bold text-primary">{formatBudget(budget?.actual)}</div>
              <div className="text-xs text-muted-foreground">de {formatBudget(budget?.estimated)} previsto</div>
              <div className="text-xs text-muted-foreground mt-1">Restante: <strong>{formatBudget(budget?.remaining)}</strong></div>
            </div>
            <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
              <div className={`h-full rounded-full ${totalLight.bar}`} style={{ width: `${Math.min(totalRate, 100)}%` }} />
            </div>
            <div className={`flex items-start gap-1.5 text-xs p-2 rounded ${deviation > 10000 ? 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400' : deviation < -10000 ? 'bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400' : 'bg-muted text-muted-foreground'}`}>
              {deviation > 10000 ? <TrendingUp className="w-3 h-3 mt-0.5 shrink-0" /> : deviation < -10000 ? <TrendingDown className="w-3 h-3 mt-0.5 shrink-0" /> : <Activity className="w-3 h-3 mt-0.5 shrink-0" />}
              {deviationText}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

const COLORS = ["#009B77", "#84CC16", "#F97316", "#64748B", "#14B8A6"];

// Format budget values - use M€ for values >= 900K
const formatBudget = (value) => {
  if (!value && value !== 0) return "0€";
  if (Math.abs(value) >= 900000) {
    return `${(value / 1000000).toFixed(2)}M€`;
  }
  return `${(value / 1000).toFixed(0)}K€`;
};

const statusLabels = {
  idea: "Ideas",
  backlog: "Backlog",
  in_progress: "En Progreso",
  pilot: "Piloto",
  scaling: "Escalando",
  completed: "Completados",
  cancelled: "Cancelados",
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [initiatives, setInitiatives] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [executive, setExecutive] = useState(null);
  const [portfolioTrend, setPortfolioTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthDetail, setMonthDetail] = useState(null);
  const [monthDetailLoading, setMonthDetailLoading] = useState(false);
  const { token, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, funnel, initiativesData, alertsData, execData, trendRes] = await Promise.all([
          api.getDashboardStats(token),
          api.getFunnelData(token),
          api.getInitiatives(token),
          api.getAlerts(token, true),
          api.getExecutiveDashboard(token),
          api.getPortfolioTrend(token).catch(() => null),
        ]);
        setStats(statsData);
        setFunnelData(funnel);
        setInitiatives(initiativesData.slice(0, 5));
        setAlerts(alertsData.slice(0, 5));
        setExecutive(execData);
        setPortfolioTrend(trendRes);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // Fix 2026-08-28: refresca al recuperar el foco de la pestaña, igual que
    // Inicio, para evitar comparar un Cockpit fresco con un Inicio (o viceversa)
    // dejado abierto sin recargar durante horas.
    const onFocus = () => fetchData();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [token]);

  const funnelChartData = funnelData
    ? Object.entries(funnelData).map(([status, count]) => ({
        name: statusLabels[status] || status,
        value: count,
      }))
    : [];

  // Tendencia del Portfolio (fix 2026-08-24): antes eran 6 meses FABRICADOS
  // (stats.initiatives_count + offset inventado sumando 1/2/3... por mes, siempre
  // creciente aunque no hubiera datos reales). Ahora viene de
  // /dashboard/portfolio-trend, que agrega initiatives.created_at/pilots.created_at
  // de verdad (acumulado real por mes). Si el endpoint falla, no se fabrica nada —
  // se oculta la gráfica (ver `trendData` más abajo).
  const trendData = portfolioTrend?.trend || null;

  // Filtro por Tendencia (petición 2026-08-24): clic en un mes del gráfico → detalle
  // real de qué iniciativas/pilotos se crearon exactamente ese mes.
  const handleTrendMonthClick = async (chartEvent) => {
    const label = chartEvent?.activeLabel;
    if (!label || !trendData) return;
    const entry = trendData.find((m) => m.month === label);
    if (!entry?.month_key) return;
    setMonthDetailLoading(true);
    setMonthDetail({ month_key: entry.month_key, month_label: label, initiatives: [], pilots: [] });
    try {
      const detail = await api.getPortfolioTrendDetail(token, entry.month_key);
      setMonthDetail({ ...detail, month_label: label });
    } catch (error) {
      toast.error("No se pudo cargar el detalle de ese mes");
      setMonthDetail(null);
    } finally {
      setMonthDetailLoading(false);
    }
  };

  const roiChartData = executive?.rd_economics ? [
    { name: "Inversión", value: executive.rd_economics.total_spend / 1000000, fill: "#64748B" },
    { name: "Retorno", value: executive.rd_economics.total_benefits / 1000000, fill: "#009B77" },
  ] : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Cockpit de Innovación</h1>
          <p className="text-muted-foreground mt-1">
            Vista ejecutiva del portfolio de innovación
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/export/full-report`, '_blank')}>
            <Download className="w-4 h-4 mr-2" />
            Exportar Excel
          </Button>
          <Button variant="outline" onClick={() => navigate("/reports")}>
            <Sparkles className="w-4 h-4 mr-2" />
            Generar Informe IA
          </Button>
          <Button onClick={() => navigate("/portfolio")}>
            Ver Portfolio
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* KPI Cards - Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="kpi-card card-hover" data-testid="kpi-initiatives">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="overline">Iniciativas Activas</p>
                <p className="text-4xl font-bold mt-2">{stats?.active_initiatives || 0}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  de {stats?.initiatives_count || 0} totales
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card card-hover" data-testid="kpi-pilots">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="overline">Pilotos en Curso</p>
                <p className="text-4xl font-bold mt-2">{stats?.pilots_count || 0}</p>
                <p className="text-sm text-muted-foreground mt-1">pendientes + extendidos (colección Pilotos, no cuenta iniciativas en fase "Piloto")</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                <Beaker className="w-6 h-6 text-accent" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card card-hover cursor-pointer" onClick={() => navigate('/pg07')} data-testid="kpi-risks">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="overline">Riesgos Críticos</p>
                <p className="text-4xl font-bold mt-2 text-destructive">
                  {stats?.critical_risks || 0}
                </p>
                <p className="text-sm text-muted-foreground mt-1">proyectos "No conforme" en PG-07</p>
                {stats?.pg07_unevaluated > 0 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate('/pg07?status=sin_evaluar'); }}
                    className="text-xs text-amber-600 mt-1 hover:underline text-left"
                    data-testid="kpi-risks-unevaluated"
                  >
                    + {stats.pg07_unevaluated} sin evaluar todavía (no es "riesgo cero") →
                  </button>
                )}
              </div>
              <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card card-hover" data-testid="kpi-partners">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="overline">Partners Curados</p>
                <p className="text-4xl font-bold mt-2">{stats?.partners_count || 0}</p>
                <p className="text-sm text-muted-foreground mt-1">curados por el equipo</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-secondary" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Executive ROI Section */}
      {executive && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* ROI I+D Card */}
          <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/5 border-green-200 dark:border-green-900">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                ROI Histórico I+D
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-4">
                <div className="text-5xl font-bold text-green-600">
                  {executive.rd_economics?.roi_percentage || 0}%
                </div>
                <div className="text-sm text-muted-foreground pb-2">
                  <div>Invertido: {(executive.rd_economics?.total_spend / 1000000).toFixed(1)}M€</div>
                  <div>Retorno: {(executive.rd_economics?.total_benefits / 1000000).toFixed(1)}M€</div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
                <div className="p-2 bg-green-500/10 rounded">
                  <div className="font-semibold">{(executive.rd_economics?.subsidy / 1000000).toFixed(1)}M€</div>
                  <div className="text-muted-foreground">Subvenciones</div>
                </div>
                <div className="p-2 bg-green-500/10 rounded">
                  <div className="font-semibold">{(executive.rd_economics?.tax_deduction / 1000000).toFixed(1)}M€</div>
                  <div className="text-muted-foreground">Deducciones</div>
                </div>
                <div className="p-2 bg-green-500/10 rounded">
                  <div className="font-semibold">{(executive.rd_economics?.financial_savings / 1000000).toFixed(1)}M€</div>
                  <div className="text-muted-foreground">Ahorro</div>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-3" data-testid="roi-source-note">
                ROI financiero sobre el libro I+D (rd_project_balances, {executive.rd_economics?.project_count || 0} proyectos, sin Barad-dûr).
                Distinto del "ROI ×N" de /impacto (múltiplo de valor de todo el portfolio). Calculado ahora: {new Date().toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}.
              </p>
            </CardContent>
          </Card>

          {/* Budget Execution Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Euro className="w-5 h-5 text-primary" />
                Presupuesto 2026
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Total Estimado</span>
                  <span className="font-mono font-semibold">
                    {formatBudget(executive.innovation_budget_2026?.estimated)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Ejecutado</span>
                  <span className="font-mono font-semibold text-green-600">
                    {formatBudget(executive.innovation_budget_2026?.actual)}
                  </span>
                </div>
                <Progress value={executive.innovation_budget_2026?.execution_rate || 0} className="h-2" />
                <div className="text-center text-sm">
                  <span className={`font-medium ${(executive.innovation_budget_2026?.execution_rate || 0) >= 50 ? 'text-green-600' : 'text-amber-600'}`}>
                    {executive.innovation_budget_2026?.execution_rate || 0}% ejecutado
                  </span>
                </div>
                {/* Breakdown */}
                <div className="mt-3 pt-3 border-t space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">• Innovación Abierta</span>
                    <div className="text-right">
                      <span className="font-mono text-green-600">{formatBudget(executive.innovation_budget_2026?.breakdown?.open_innovation?.actual)}</span>
                      <span className="text-muted-foreground"> / {formatBudget(executive.innovation_budget_2026?.breakdown?.open_innovation?.estimated)}</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">• Proyectos I+D</span>
                    <div className="text-right">
                      <span className="font-mono text-green-600">{formatBudget(executive.innovation_budget_2026?.breakdown?.rd_projects?.actual)}</span>
                      <span className="text-muted-foreground"> / {formatBudget(executive.innovation_budget_2026?.breakdown?.rd_projects?.estimated)}</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs pt-2 border-t">
                    <span className="text-muted-foreground">Pendiente</span>
                    <span className="font-mono font-medium">{formatBudget(executive.innovation_budget_2026?.remaining)}</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-2 text-center">
                <div>
                  <div className="text-2xl font-bold">{executive.pilots?.total || 0}</div>
                  <div className="text-xs text-muted-foreground">Pilotos totales</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">{executive.pilots?.scaled || 0}</div>
                  <div className="text-xs text-muted-foreground">Escalados</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Upcoming Milestones Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Próximos Hitos (90 días)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {executive.upcoming_milestones?.length > 0 ? (
                <div className="space-y-2">
                  {executive.upcoming_milestones.slice(0, 4).map((milestone, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-muted/50 rounded text-sm">
                      <div className={`w-2 h-2 rounded-full ${
                        milestone.status === 'in_progress' ? 'bg-amber-500' : 'bg-blue-500'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{milestone.name}</p>
                        <p className="text-xs text-muted-foreground">{milestone.responsible}</p>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {milestone.days_remaining}d
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground text-sm">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  No hay hitos próximos
                </div>
              )}
              <Button variant="ghost" size="sm" className="w-full mt-2" onClick={() => navigate("/gantt")}>
                Ver Cronograma Completo
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Budget Comparison Panel - OI vs I+D */}
      {executive?.innovation_budget_2026 && (
        <BudgetComparisonPanel executive={executive} />
      )}

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Funnel Chart */}
        <Card className="lg:col-span-2" data-testid="funnel-chart">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              Funnel de Innovación
            </CardTitle>
            <CardDescription>
              Distribución de iniciativas por estado en el ciclo de innovación
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={funnelChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, value, percent }) => percent > 0.05 ? `${name.length > 10 ? name.slice(0, 10) + '…' : name}: ${value}` : ''}
                    labelLine={false}
                  >
                    {funnelChartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <Card data-testid="upcoming-milestones">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-primary" />
              Próximos Hitos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div>
                <p className="font-medium">Entregables pendientes</p>
                <p className="text-sm text-muted-foreground">próximos 10 días</p>
              </div>
              <Badge variant="secondary" className="text-lg">
                {stats?.upcoming_deliverables || 0}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div>
                <p className="font-medium">Proyectos I+D</p>
                <p className="text-sm text-muted-foreground">balances 2026 (mismo total que /projects)</p>
              </div>
              <Badge variant="secondary" className="text-lg">
                {stats?.projects_count || 0}
              </Badge>
            </div>
            {isAdmin && executive?.innovation_budget_2026 && (
              <div className="p-3 bg-primary/5 rounded-lg border border-primary/20" data-testid="control-presupuestario">
                <div className="flex items-center gap-2 mb-2">
                  <Euro className="w-4 h-4 text-primary" />
                  <p className="font-medium">Control Presupuestario</p>
                  <span className="text-xs text-muted-foreground ml-auto">= Finanzas</span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Planificado</span>
                    <span className="font-mono">
                      €{Math.round(executive.innovation_budget_2026.estimated || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Ejecutado</span>
                    <span className="font-mono">
                      €{Math.round(executive.innovation_budget_2026.actual || 0).toLocaleString()}
                    </span>
                  </div>
                  <Progress
                    value={executive.innovation_budget_2026.execution_rate || 0}
                    className="h-2"
                  />
                  <p className="text-[11px] text-muted-foreground pt-1" data-testid="budget-vs-annual-id-note-dashboard">
                    Plan de Innovación 2026 — distinto del recorte anual I+D certificado (888.896,05€/347.012,68€, Excel AU:AW).
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Trend and Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trend Chart */}
        <Card data-testid="trend-chart">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Tendencia del Portfolio
            </CardTitle>
            <CardDescription>
              Evolución real de iniciativas y pilotos (acumulado por mes, según fecha de creación)
              {trendData && " — haz clic en un mes para ver el detalle"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!trendData ? (
              <div className="h-64 flex items-center justify-center text-sm text-muted-foreground" data-testid="trend-chart-empty">
                Sin histórico fiable disponible por ahora.
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trendData} onClick={handleTrendMonthClick} className="cursor-pointer" data-testid="trend-chart-area">
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="month" className="text-xs" />
                    <YAxis className="text-xs" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="initiatives"
                      stackId="1"
                      stroke="#009B77"
                      fill="#009B77"
                      fillOpacity={0.6}
                      name="Iniciativas"
                    />
                    <Area
                      type="monotone"
                      dataKey="pilots"
                      stackId="2"
                      stroke="#84CC16"
                      fill="#84CC16"
                      fillOpacity={0.6}
                      name="Pilotos"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Alerts */}
        <Card data-testid="ai-alerts">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-accent" />
              Alertas IA
            </CardTitle>
            <CardDescription>Innovation Radar - Oportunidades detectadas</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {alerts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No hay alertas pendientes</p>
              </div>
            ) : (
              alerts.map((alert) => (
                <div
                  key={alert.id}
                  className="p-3 rounded-lg border border-border/50 hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => navigate("/alerts")}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-2 h-2 rounded-full mt-2 ${
                        alert.severity === "high"
                          ? "bg-destructive"
                          : alert.severity === "medium"
                          ? "bg-amber-500"
                          : "bg-primary"
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{alert.title}</p>
                      <p className="text-sm text-muted-foreground truncate">
                        {alert.message}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => navigate("/alerts")}
            >
              Ver todas las alertas
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Recent Initiatives */}
      <Card data-testid="recent-initiatives">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Leaf className="w-5 h-5 text-primary" />
                Iniciativas Recientes
              </CardTitle>
              <CardDescription>Últimas actualizaciones del portfolio</CardDescription>
            </div>
            <Button variant="outline" onClick={() => navigate("/portfolio")}>
              Ver todas
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {initiatives.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Briefcase className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No hay iniciativas aún</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => navigate("/portfolio")}
                >
                  Crear primera iniciativa
                </Button>
              </div>
            ) : (
              initiatives.map((initiative) => (
                <div
                  key={initiative.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-border/50 hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/initiative/${initiative.id}`)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      {initiative.type === "pilot" ? (
                        <Beaker className="w-5 h-5 text-primary" />
                      ) : initiative.type === "rd" ? (
                        <FolderKanban className="w-5 h-5 text-primary" />
                      ) : (
                        <Briefcase className="w-5 h-5 text-primary" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{initiative.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {initiative.description?.slice(0, 60)}
                        {initiative.description?.length > 60 ? "..." : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {initiative.esg_tags?.length > 0 && (
                      <div className="hidden md:flex gap-1">
                        {initiative.esg_tags.slice(0, 2).map((tag, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                    <span
                      className={`status-badge status-${initiative.status?.replace(
                        "_",
                        "-"
                      )}`}
                    >
                      {statusLabels[initiative.status] || initiative.status}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Detalle de mes (Tendencia del Portfolio) — petición 2026-08-24 */}
      <Dialog open={!!monthDetail} onOpenChange={(open) => !open && setMonthDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="trend-month-detail-dialog">
          <DialogHeader>
            <DialogTitle>Creado en {monthDetail?.month_label}</DialogTitle>
            <DialogDescription>
              Iniciativas y pilotos con fecha de creación real en este mes (no acumulado).
            </DialogDescription>
          </DialogHeader>
          {monthDetailLoading ? (
            <p className="text-sm text-muted-foreground py-4">Cargando...</p>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                  Iniciativas ({monthDetail?.initiatives?.length || 0})
                </p>
                {!monthDetail?.initiatives?.length ? (
                  <p className="text-sm text-muted-foreground">Ninguna creada este mes.</p>
                ) : (
                  <ul className="space-y-1" data-testid="trend-month-initiatives-list">
                    {monthDetail.initiatives.map((i) => (
                      <li key={i.id} className="text-sm flex items-center justify-between border-b py-1">
                        <span>{i.name}</span>
                        <Badge variant="outline" className="text-xs">{statusLabels[i.status] || i.status}</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                  Pilotos ({monthDetail?.pilots?.length || 0})
                </p>
                {!monthDetail?.pilots?.length ? (
                  <p className="text-sm text-muted-foreground">Ninguno creado este mes.</p>
                ) : (
                  <ul className="space-y-1" data-testid="trend-month-pilots-list">
                    {monthDetail.pilots.map((p) => (
                      <li key={p.id} className="text-sm flex items-center justify-between border-b py-1">
                        <span>{p.name}</span>
                        <Badge variant="outline" className="text-xs">{p.decision}</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
